import React, { useEffect } from 'react';
import PropTypes from "prop-types";
import { useSelector, useDispatch } from 'react-redux';
import {
	reduxForm,
	Field,
	initialize,
	change,
	getFormValues,
	getFormSyncErrors,
	getFormAsyncErrors,
	getFormError
} from 'redux-form';
import {
	Modal,
	Button,
	Icon,
	ProgressIndicator,
	ErrorBanner,
	ReduxControls
} from 'rhombus-react';

import { useTooltipRebuild } from 'hooks/useTooltipRebuild';

import { noop, splitFilename, TextOverflow } from 'utils/common';

import { validFilenameCharacters } from 'widgets/forms/validators/validators';
import { creativeFilenameMaxLength } from 'widgets/forms/validators/Creatives';
import { allNamesUnique, baseNameRequiredMultiple } from 'widgets/modals/assets/BulkUpload/validators';
import { asyncValidateMultipleRename, uniqueFilenameMultiple } from 'widgets/modals/creatives/bulk/upload/validators';

import "./RenameMultipleModal.css";

const RENAME_MULTIPLE_CREATIVES = "rename-multiple-creatives";

export function formatFilename(value) {
	let [base] = splitFilename(value.newName);
	return base;
}

export function normalizeFilename(value, prevValue) {
	let { extension } = prevValue;

	return {
		...prevValue,
		newName: `${value}${extension}`,
	};
}

export function generateInitialValues(conflicts, suffix) {
	const filenames = conflicts.reduce((prev, conflict, index) => {
		const { filename } = conflict;
		const [base, extension] = splitFilename(filename);

		prev[index] = {
			original: filename,
			newName: `${base}${suffix}${extension}`,
			extension,
		};

		return prev;
	}, {});

	return {
		suffix,
		filenames,
		alwaysDirty: false,
	};
}

function newNameAllowedChars(value){
	return validFilenameCharacters(value?.newName);
}

function newNameMaxLength(value){
	return creativeFilenameMaxLength(value?.newName);
}

export function NameFields({ filenames = {} }) {
	const entries = Object.entries(filenames);

	return entries.map(([key, value]) => {
		const { original, extension } = value || {};
		return (
			<Field
				key={key}
				name={`filenames[${key}]`}
				label={original}
				component={ReduxControls.Input}
				post={extension}
				format={formatFilename}
				normalize={normalizeFilename}
				validate={[
					baseNameRequiredMultiple,
					uniqueFilenameMultiple,
					newNameMaxLength,
					newNameAllowedChars,
				]}
			/>
		);
	});
}

export function buildErrorMessages(errors = {}, filenameValues = {}) {
	const { filenames = {} } = errors || {};
	const errorMessages = Object.entries(filenames).reduce((prev, [key, value]) => {
		if(!value){
			/*
				If a field had an error at some point, but the user resolved it, `value` will be
				undefined, but there will still be an entry for it, so we return early to skip those.
			*/
			return prev;
		}
		const { original } = filenameValues[key] || {};
		prev.push(
			<div className="name-error" key={key}>
				<div className="original-name"><TextOverflow value={original} /></div>
				<div className="message"> - {value}</div>
			</div>
		);
		return prev;
	}, []);

	return errorMessages;
}

function useErrorBanner(formName, filenameValues = {}) {
	const errors = useSelector((state) => {
		return {
			syncErrors: getFormSyncErrors(formName)(state),
			asyncErrors: getFormAsyncErrors(formName)(state),
			formError: getFormError(formName)(state),
		};
	});

	const errorMessages = [];

	if(errors.formError){
		errorMessages.push(errors.formError);
	}
	errorMessages.push(...buildErrorMessages(errors.syncErrors, filenameValues));
	errorMessages.push(...buildErrorMessages(errors.asyncErrors, filenameValues));

	if(!errorMessages.length) {
		return null;
	}

	return (
		<ErrorBanner
			messages={errorMessages}
			title="Sorry, please fix the errors below and try again."
		/>
	);
}

function RenameMultipleModal(props) {
	const {
		conflicts,
		onBack,
		onMultipleRename,

		// Redux form props
		handleSubmit,
		asyncValidating,
		valid,
	} = props;

	const dispatch = useDispatch();
	const {
		suffix,
		filenames,
	} = useSelector(getFormValues(RENAME_MULTIPLE_CREATIVES)) || {};

	function reinitialize() {
		let initialValues = generateInitialValues(conflicts, suffix);
		dispatch(initialize(RENAME_MULTIPLE_CREATIVES, initialValues));

		dispatch(change(RENAME_MULTIPLE_CREATIVES, "alwaysDirty", true)); /*
			If you have a pristine form, redux-form doesn't bother to async validate on submit,
			so we always set this flag to false on initialize, and then always set it to true on
			reinitialize, to ensure that the form always has a dirty value, and always validates.
		*/
	}

	let $errorBanner = useErrorBanner(RENAME_MULTIPLE_CREATIVES, filenames);

	useTooltipRebuild();
	useEffect(reinitialize, []);

	function submitMultipleRename(values) {
		return onMultipleRename(values.filenames);
	}

	function renderContent() {
		return (
			<div className="c-modal-body">
				<div className="main-content">
					<ProgressIndicator isLoading={asyncValidating} />
					<form id={RENAME_MULTIPLE_CREATIVES} onSubmit={handleSubmit(submitMultipleRename)}>
						<div className="fields">
							{$errorBanner}
							<div className="suffix-controls">
								<Field
									name="suffix"
									className="suffix-field"
									component={ReduxControls.Input}
									label="Append to All Names"
								/>
								<Icon
									className="reapply-button"
									name="fas fa-undo"
									data-tip="Reset and apply appended value to creative file names"
									onClick={reinitialize}
								/>
							</div>
							<hr />
							<div className="name-fields">
								<NameFields
									filenames={filenames}
								/>
							</div>
						</div>
					</form>
				</div>
			</div>
		);
	}

	function renderFooter() {
		return (
			<>
				<Button onClick={() => onBack()} theme="default">Back</Button>
				<Button
					type="submit"
					form={RENAME_MULTIPLE_CREATIVES}
					theme="blue"
					disabled={Boolean(!valid || asyncValidating)}
				>
					Rename Creatives
				</Button>
			</>
		);
	}

	const modalProps = {
		type: "modal",
		title: `Rename Uploaded Creative Files`,
		setAppTitle: false,
		allowFullScreen: false,
		className: "creative-upload-error-modal rename-multiple",
		xClose: false,
		bladeProps: {
			verticallyCenter: true,
		},
		renderContent,
		renderFooter,
	};

	return <Modal {...modalProps} />;
}

RenameMultipleModal.displayName = "RenameMultipleModal";

RenameMultipleModal.propTypes = {
	// Some of these props aren't used in this file, but are necessary for validation
	conflicts: PropTypes.arrayOf(PropTypes.shape({
		filename: PropTypes.string,
	})),
	previousNames: PropTypes.arrayOf(PropTypes.string),

	onBack: PropTypes.func,
	onMultipleRename: PropTypes.func,

	asyncValidating: PropTypes.bool,
};

RenameMultipleModal.defaultProps = {
	conflicts: [],
	previousNames: [],

	onBack: noop,
	onMultipleRename: noop,

	asyncValidating: false,
};

export default reduxForm({
	form: RENAME_MULTIPLE_CREATIVES,
	initialValues: {
		suffix: "_copy",
		alwaysDirty: false,
	},
	validate: allNamesUnique,
	asyncValidate: asyncValidateMultipleRename,
	asyncBlurFields: [],
})(RenameMultipleModal);
